import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import {
  getAllConfigurations,
  getConfigurationById,
  updateConfiguration,
} from "../../Utils/apiServices";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import TextField from "../../Components/TextField";
import { Typography } from "../../Components/Typography";

const EMPTY_FORM = {
  active_idea: "",
  overall_idea: "",
  secondary_mentor_per_idea: "",
  student_per_idea: "",
  mentor_reminder_hours: "",
  admin_escalation_hours: "",
};

const Configuration = () => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const response = await getAllConfigurations();
      const payload = response?.data?.data ?? response?.data;
      if (response?.success) {
        setConfigurations(Array.isArray(payload) ? payload : payload ? [payload] : []);
      }
    } catch (err) {
      console.error("Error fetching configurations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelEdit = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setError("");
  };

  const handleStartEdit = async (config) => {
    try {
      const response = await getConfigurationById(config.id);
      const data = response?.data?.data ?? response?.data ?? config;
      if (response?.success) {
        setEditingId(config.id);
        setError("");
        setFormData({
          active_idea: data.active_idea ?? "",
          overall_idea: data.overall_idea ?? "",
          secondary_mentor_per_idea: data.secondary_mentor_per_idea ?? "",
          student_per_idea: data.student_per_idea ?? "",
          mentor_reminder_hours: data.mentor_reminder_hours ?? "",
          admin_escalation_hours: data.admin_escalation_hours ?? "",
        });
        return;
      }
    } catch (err) {
      console.error("Error fetching configuration details:", err);
    }
    // Fall back to the row data we already have
    setEditingId(config.id);
    setError("");
    setFormData({
      active_idea: config.active_idea ?? "",
      overall_idea: config.overall_idea ?? "",
      secondary_mentor_per_idea: config.secondary_mentor_per_idea ?? "",
      student_per_idea: config.student_per_idea ?? "",
      mentor_reminder_hours: config.mentor_reminder_hours ?? "",
      admin_escalation_hours: config.admin_escalation_hours ?? "",
    });
  };

  const handleUpdate = async () => {
    const fields = {
      active_idea: String(formData.active_idea).trim(),
      overall_idea: String(formData.overall_idea).trim(),
      secondary_mentor_per_idea: String(formData.secondary_mentor_per_idea).trim(),
      student_per_idea: String(formData.student_per_idea).trim(),
      mentor_reminder_hours: String(formData.mentor_reminder_hours).trim(),
      admin_escalation_hours: String(formData.admin_escalation_hours).trim(),
    };

    if (Object.values(fields).every((value) => value === "")) {
      setError("Please fill in at least one field.");
      return;
    }

    const hasInvalidNumber = Object.values(fields).some(
      (value) => value !== "" && Number.isNaN(Number(value))
    );
    if (hasInvalidNumber) {
      setError("All configuration values must be valid numbers.");
      return;
    }

    const payload = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [
        key,
        value === "" ? null : Number(value),
      ])
    );

    setSaving(true);
    try {
      const response = await updateConfiguration(editingId, payload);
      if (response?.success) {
        await fetchConfigurations();
        handleCancelEdit();
      } else {
        setError(response?.error || "Failed to update configuration.");
      }
    } catch (err) {
      console.error("Error updating configuration:", err);
      setError(err?.error || "An error occurred while updating the configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col w-full gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <Typography as="h1" className="text-2xl font-bold text-primary-900 tracking-tight">
            Configuration
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600">
            Manage the active idea, overall idea, and per-idea mentor and student limits.
          </Typography>
        </div>
      </div>

      {/* Edit Form - only shown while editing */}
      {editingId && (
        <Card className="w-full">
          <div className="flex flex-col gap-5">
            <Typography as="h2" className="text-lg font-semibold text-primary-900">
              Edit Configuration
            </Typography>

            <TextField
              label="Active Idea"
              name="active_idea"
              type="number"
              value={formData.active_idea}
              onChange={(e) => handleInputChange("active_idea", e.target.value)}
              placeholder="Enter the active idea value"
            />

            <TextField
              label="Overall Idea"
              name="overall_idea"
              type="number"
              value={formData.overall_idea}
              onChange={(e) => handleInputChange("overall_idea", e.target.value)}
              placeholder="Enter the overall idea value"
            />

            <TextField
              label="Secondary Mentors Per Idea"
              name="secondary_mentor_per_idea"
              type="number"
              value={formData.secondary_mentor_per_idea}
              onChange={(e) =>
                handleInputChange("secondary_mentor_per_idea", e.target.value)
              }
              placeholder="Enter the max secondary mentors per idea"
            />

            <TextField
              label="Students Per Idea"
              name="student_per_idea"
              type="number"
              value={formData.student_per_idea}
              onChange={(e) => handleInputChange("student_per_idea", e.target.value)}
              placeholder="Enter the max students per idea"
            />

            <TextField
              label="Mentor Reminder (hours)"
              name="mentor_reminder_hours"
              type="number"
              value={formData.mentor_reminder_hours}
              onChange={(e) => handleInputChange("mentor_reminder_hours", e.target.value)}
              placeholder="Hours before reminding the assigned mentor (default 48)"
            />

            <TextField
              label="Admin Escalation (hours)"
              name="admin_escalation_hours"
              type="number"
              value={formData.admin_escalation_hours}
              onChange={(e) => handleInputChange("admin_escalation_hours", e.target.value)}
              placeholder="Hours before escalating to admin (default 72)"
            />

            {error && (
              <Typography as="p" className="text-sm text-red-500">
                {error}
              </Typography>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button
                variant="secondary"
                size="medium"
                onClick={handleCancelEdit}
                disabled={saving}
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={handleUpdate}
                disabled={saving}
                startIcon={
                  saving ? (
                    <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
                  ) : (
                    <Icon icon="lucide:save" className="h-5 w-5" />
                  )
                }
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {saving ? "Saving..." : "Update"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-28 w-full rounded-2xl bg-slate-100" />
        </div>
      ) : configurations.length === 0 ? (
        <Card className="w-full">
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center">
              <Icon icon="lucide:settings" className="h-7 w-7 text-slate-400" />
            </div>
            <Typography as="p" className="text-sm text-tertiary-600">
              No configuration available to edit.
            </Typography>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {configurations.map((config) => (
            <Card key={config.id} className="w-full">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-4 flex-1">
                  <div className="flex flex-col gap-1">
                    <Typography as="h4" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                      Active Idea
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap">
                      {config.active_idea ?? <span className="italic text-slate-400">Not set</span>}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography as="h4" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                      Overall Idea
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap">
                      {config.overall_idea ?? <span className="italic text-slate-400">Not set</span>}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography as="h4" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                      Secondary Mentors Per Idea
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap">
                      {config.secondary_mentor_per_idea ?? <span className="italic text-slate-400">Not set</span>}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography as="h4" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                      Students Per Idea
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap">
                      {config.student_per_idea ?? <span className="italic text-slate-400">Not set</span>}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography as="h4" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                      Mentor Reminder (hours)
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap">
                      {config.mentor_reminder_hours ?? <span className="italic text-slate-400">Not set (default 48)</span>}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography as="h4" className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                      Admin Escalation (hours)
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-wrap">
                      {config.admin_escalation_hours ?? <span className="italic text-slate-400">Not set (default 72)</span>}
                    </Typography>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="secondary"
                    size="small"
                    startIcon={<Icon icon="lucide:pencil" className="h-4 w-4" />}
                    onClick={() => handleStartEdit(config)}
                    className="border-slate-200 text-slate-700 hover:bg-slate-50"
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Configuration;
